import React, { useState, useEffect } from 'react';
import {
   Box,
   Typography,
   TextField,
   Button,
   Link,
   InputAdornment,
   IconButton,
   Snackbar,
   Alert,
   CircularProgress,
   Card,
   Chip,
   Tabs,
   Tab,
   CardContent,
   Divider,
} from '@mui/material';
import {
   Email as EmailIcon,
   Lock as LockIcon,
   Visibility as VisibilityIcon,
   VisibilityOff as VisibilityOffIcon,
   Phone as PhoneIcon,
   Security as SecurityIcon,
} from '@mui/icons-material';
import CelebrationIcon from '@mui/icons-material/Celebration';
import { Link as RouterLink, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
   getAuth,
   signInWithEmailAndPassword,
   sendPasswordResetEmail,
   sendEmailVerification,
   signInWithCustomToken,
} from 'firebase/auth';
import { db } from '../firebase/config';
import {
   doc,
   setDoc,
   getDoc,
   updateDoc,
   getDocs,
   collection,
   where,
   query,
} from 'firebase/firestore';
import backgroundImage from '../assets/images/travel-background';
import AuthLayout from './AuthLayout';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { checkInvitedUser, cleanupInvitedUser } from '../services/invitedUserService';
import logo from '../assets/images/logo.png';
import {
   verifyPhoneInvitation,
   formatPhoneNumberForDisplay,
} from '../services/phoneInvitationService';
import {
   trackUserAction,
   trackAnonymousPageView,
   trackAnonymousAction,
   ACTION_TYPES,
   ACTION_CATEGORIES,
   createAuthMetadata,
   createErrorMetadata,
   createPageMetadata,
} from '../utils/actionTracker';
import PhoneInput from '../components/common/PhoneInput';
import { Country, COUNTRIES } from '../data/countries';
// Removed Firebase phone auth service - now using Twilio backend API
// import phoneAuthService from '../services/phoneAuthService';

const LoginPage: React.FC = () => {
   const navigate = useNavigate();
   const location = useLocation();
   const [searchParams] = useSearchParams();
   const auth = getAuth();
   const { signIn } = useAuth();
   const { refreshInvitationStatus } = useSubscription();

   // Get return URL from query params first, then location state, then default to dashboard
   const returnUrl = searchParams.get('returnUrl') || '';

   // Form state
   const [email, setEmail] = useState('');
   const [password, setPassword] = useState('');
   const [showPassword, setShowPassword] = useState(false);
   const [loading, setLoading] = useState(false);

   // Phone login state
   const [phoneNumber, setPhoneNumber] = useState('');
   const [selectedCountry, setSelectedCountry] = useState<Country>(COUNTRIES[0]); // Default to US
   const [verificationCode, setVerificationCode] = useState('');
   const [codeSent, setCodeSent] = useState(false);
   const [tabValue, setTabValue] = useState(0);

   // Invitation state
   const [invitationToken, setInvitationToken] = useState<string | null>(null);
   const [invitationData, setInvitationData] = useState<any>(null);

   // Error handling
   const [emailError, setEmailError] = useState<string | null>(null);
   const [passwordError, setPasswordError] = useState<string | null>(null);

   // Toast notifications
   const [showToast, setShowToast] = useState(false);
   const [toastMessage, setToastMessage] = useState('');
   const [toastSeverity, setToastSeverity] = useState<'success' | 'error' | 'warning' | 'info'>(
      'success'
   );

   // Track page view on mount
   useEffect(() => {
      trackAnonymousPageView('/login', 'Login Page', {
         action: 'login_page_visit',
         source: 'login_page',
         context: 'authentication',
      });
   }, []);

   // Helper functions for toast notifications
   const showSuccessToast = (message: string, duration: number = 4000) => {
      setToastMessage(message);
      setToastSeverity('success');
      setShowToast(true);
      // Auto-hide success toast
      setTimeout(() => {
         setShowToast(false);
      }, duration);
   };

   const showErrorToast = (message: string, duration: number = 10000) => {
      setToastMessage(message);
      setToastSeverity('error');
      setShowToast(true);
      // Auto-hide error toast after duration
      setTimeout(() => {
         setShowToast(false);
      }, duration);
   };

   const showInfoToast = (message: string, duration: number = 5000) => {
      setToastMessage(message);
      setToastSeverity('info');
      setShowToast(true);
      // Auto-hide info toast after duration
      setTimeout(() => {
         setShowToast(false);
      }, duration);
   };

   const handleToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') {
         return;
      }
      setShowToast(false);
   };

   // Handle invitation token and phone number from URL params
   useEffect(() => {
      const invitation = searchParams.get('invitation');
      const phone = searchParams.get('phone');

      if (invitation) {
         setInvitationToken(invitation);
         verifyInvitationData(invitation);
      }

      if (phone) {
         setPhoneNumber(phone);
         setTabValue(1); // Switch to phone tab
      }
   }, [searchParams]);

   const verifyInvitationData = async (token: string) => {
      try {
         const result = await verifyPhoneInvitation(token);
         if (result.success && result.invitation) {
            setInvitationData(result.invitation);
         }
      } catch (error) {
         console.error('Error verifying invitation:', error);
      }
   };

   // Simple form validation
   const validateForm = () => {
      let isValid = true;

      // Reset errors
      setEmailError(null);
      setPasswordError(null);

      // Validate email
      if (!email.trim()) {
         setEmailError('Please enter your email');
         isValid = false;
      }

      // Validate password
      if (!password) {
         setPasswordError('Please enter your password');
         isValid = false;
      }

      return isValid;
   };

   // Handle successful login navigation with invitation and subscription logic
   const handleSuccessfulLogin = async (isInvited: boolean = false) => {
      const userEmail = auth.currentUser?.email;

      // Simplified logic: Check invitation status first
      if (isInvited) {
         // CRITICAL FIX: Refresh invitation status in SubscriptionContext to ensure consistency
         try {
            await refreshInvitationStatus();
         } catch (error) {
            // Don't fail the login process if this fails
         }

         // Navigate immediately without blocking operations
         if (returnUrl) {
            const decodedUrl = decodeURIComponent(returnUrl);
            navigate(decodedUrl, { replace: true });
         } else {
            navigate('/dashboard', { replace: true });
         }
         return;
      }

      // For all users (invited or not): Navigate directly to dashboard or returnUrl
      // No subscription check - users can access dashboard without payment
      if (returnUrl) {
         const decodedUrl = decodeURIComponent(returnUrl);
         navigate(decodedUrl, { replace: true });
      } else {
         navigate('/dashboard', { replace: true });
      }
   };

   // Send phone verification code using Twilio backend API
   const sendPhoneVerificationCode = async () => {
      if (!phoneNumber.trim()) {
         showErrorToast('Please enter your phone number');
         return;
      }

      setLoading(true);

      // Clean and format phone number properly
      let cleanedPhoneNumber = phoneNumber.replace(/\D/g, ''); // Remove all non-digits

      // Combine country code with phone number
      const fullPhoneNumber = selectedCountry.dialCode + cleanedPhoneNumber;

      try {
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/phone/send-verification`,
            {
               method: 'POST',
               headers: {
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({
                  phoneNumber: fullPhoneNumber,
                  purpose: 'login', // Specify this is for login, not signup
               }),
            }
         );

         const data = await response.json();

         if (data.success) {
            setCodeSent(true);
            showSuccessToast('Verification code sent! Please check your phone.', 4000);
         } else {
            showErrorToast(data.error || 'Failed to send verification code');
         }
      } catch (error: any) {
         showErrorToast('Failed to send verification code. Please try again.');
      } finally {
         setLoading(false);
      }
   };

   // Verify phone code and login using Twilio backend API
   const verifyPhoneAndLogin = async () => {
      if (!verificationCode.trim()) {
         showErrorToast('Please enter the verification code');
         return;
      }

      setLoading(true);

      // Combine country code with phone number
      const fullPhoneNumber = selectedCountry.dialCode + phoneNumber;

      try {
         // First try to login with linked phone number
         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/phone/login-linked`, {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
            },
            body: JSON.stringify({
               phoneNumber: fullPhoneNumber,
               verificationCode: verificationCode,
            }),
         });

         const data = await response.json();

         if (data.success && data.customToken) {
            // Sign in with the custom token from backend
            const userCredential = await signInWithCustomToken(auth, data.customToken);
            const user = userCredential.user;

            // Handle successful login with invitation processing
            if (invitationData && invitationToken) {
               // Process invitation for phone login
               try {
                  const inviteResponse = await fetch(
                     `${process.env.REACT_APP_API_URL}/api/phone/process-invitation-login`,
                     {
                        method: 'POST',
                        headers: {
                           'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                           invitationToken: invitationToken,
                           userId: user.uid,
                        }),
                     }
                  );

                  if (inviteResponse.ok) {
                     // Navigate to the specific item from the invitation
                     if (invitationData.type === 'trip') {
                        navigate(`/trips/${invitationData.tripId}`);
                     } else if (invitationData.type === 'event') {
                        navigate(`/events/${invitationData.eventId}`);
                     } else if (invitationData.type === 'album') {
                        navigate(`/albums/${invitationData.albumId}`);
                     } else {
                        handleSuccessfulLogin(true);
                     }
                     return;
                  }
               } catch (inviteError) {
                  console.error('Error processing invitation:', inviteError);
               }
            }

            // Navigate based on invitation status
            handleSuccessfulLogin(data.isInvited);
         } else if (data.error === 'PHONE_NOT_LINKED') {
            // Phone number is not linked to any account
            showErrorToast(
               'This phone number is not linked to any account. Please use email login or link your phone number in your profile settings.'
            );
         } else {
            showErrorToast(data.error || 'Failed to verify phone number');
         }
      } catch (error: any) {
         showErrorToast('Failed to verify phone number. Please try again.');
      } finally {
         setLoading(false);
      }
   };

   // Handle login with email/password
   const handleLogin = async (event: React.FormEvent) => {
      event.preventDefault();

      if (!validateForm()) {
         return;
      }

      setLoading(true);

      // Track login attempt (anonymous since user isn't authenticated yet)
      try {
         await trackAnonymousAction(
            ACTION_TYPES.LOGIN,
            ACTION_CATEGORIES.USER,
            createAuthMetadata('login_attempt', email, 'email_password', {
               source: 'email_password_form',
            }),
            'Login attempt with email/password'
         );
      } catch (trackingError) {
         console.error('Failed to track login attempt:', trackingError);
      }

      try {
         // Use AuthContext signIn method which handles invitation checking
         const { isInvited } = await signIn(email, password);

         // Get the current user after successful sign in
         const user = auth.currentUser;
         if (!user) {
            throw new Error('User not found after sign in');
         }

         if (!user.emailVerified) {
            // Prefer Firebase Auth email (works without server SendGrid). Fallback: custom link via API.
            const continueUrl = `${window.location.origin}/login${
               returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : ''
            }`;
            try {
               await sendEmailVerification(user, {
                  url: continueUrl,
                  handleCodeInApp: false,
               });
               showErrorToast(
                  'We sent a verification link to your email. Check spam if you don’t see it.'
               );
            } catch (firebaseVerifyErr) {
               console.warn('Firebase sendEmailVerification failed, trying server:', firebaseVerifyErr);
               try {
                  const res = await fetch(
                     `${process.env.REACT_APP_API_URL}/api/user/send-verification-email`,
                     {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                           email: user.email,
                           returnUrl_path: returnUrl ? encodeURIComponent(`${returnUrl}`) : '',
                        }),
                     }
                  );
                  if (!res.ok) {
                     const data = await res.json().catch(() => ({}));
                     throw new Error(data.message || 'Could not send verification email');
                  }
                  showErrorToast(
                     'We sent a verification link to your email. Check spam if you don’t see it.'
                  );
               } catch (apiErr) {
                  console.error('Verification email failed (Firebase + API):', apiErr);
                  showErrorToast(
                     'Could not send verification email. Configure SendGrid on the server or check Firebase Auth email templates.'
                  );
               }
            }

            // Track email verification required
            try {
               await trackUserAction({
                  userId: user.uid,
                  actionType: ACTION_TYPES.LOGIN,
                  category: ACTION_CATEGORIES.USER,
                  metadata: createAuthMetadata(
                     'login_email_verification_required',
                     user.email || undefined,
                     'email_password',
                     {
                        source: 'email_password_form',
                     }
                  ),
               });
            } catch (trackingError) {
               console.error('Failed to track email verification required:', trackingError);
            }

            setLoading(false);
            return;
         }

         // Handle successful login with invitation processing
         if (invitationToken && invitationData) {
            // Process invitation for email login
            try {
               const inviteResponse = await fetch(
                  `${process.env.REACT_APP_API_URL}/api/phone/process-invitation-login`,
                  {
                     method: 'POST',
                     headers: {
                        'Content-Type': 'application/json',
                     },
                     body: JSON.stringify({
                        invitationToken: invitationToken,
                        userId: user.uid,
                     }),
                  }
               );

               if (inviteResponse.ok) {
                  // Navigate to the specific item from the invitation
                  if (invitationData.type === 'trip') {
                     navigate(`/trips/${invitationData.tripId}`);
                  } else if (invitationData.type === 'event') {
                     navigate(`/events/${invitationData.eventId}`);
                  } else if (invitationData.type === 'album') {
                     navigate(`/albums/${invitationData.albumId}`);
                  } else {
                     handleSuccessfulLogin(true);
                  }
                  return;
               }
            } catch (inviteError) {
               console.error('Error processing invitation:', inviteError);
            }
         }

         // Navigate immediately, then update data in background
         handleSuccessfulLogin(isInvited);

         // Update last login time in background (non-blocking)
         const userDocRef = doc(db, 'users', user.uid);
         getDoc(userDocRef)
            .then((userDoc) => {
               if (userDoc.exists()) {
                  return updateDoc(userDocRef, {
                     lastLogin: new Date().toISOString(),
                  });
               }
            })
            .catch((error) => console.error('Failed to update last login:', error));

         // Track successful login in background (non-blocking)
         trackUserAction({
            userId: user.uid,
            actionType: ACTION_TYPES.LOGIN,
            category: ACTION_CATEGORIES.USER,
            metadata: createAuthMetadata(
               'login_success',
               user.email || undefined,
               'email_password',
               {
                  source: 'email_password_form',
                  hasReturnUrl: !!returnUrl,
                  emailVerified: user.emailVerified,
                  isInvited: isInvited,
               }
            ),
         }).catch((trackingError) =>
            console.error('Failed to track login success:', trackingError)
         );
      } catch (error: any) {
         // Track login failure (anonymous since user authentication failed)
         try {
            await trackAnonymousAction(
               ACTION_TYPES.LOGIN,
               ACTION_CATEGORIES.USER,
               createErrorMetadata('login_failure', error.code, error.message, {
                  source: 'email_password_form',
                  context: 'authentication',
                  email: email,
                  loginMethod: 'email_password',
               }),
               'Login failure with email/password'
            );
         } catch (trackingError) {
            console.error('Failed to track login failure:', trackingError);
         }

         if (error.code === 'auth/invalid-credential') {
            showErrorToast('Invalid email or password. Please try again.');
         } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
            showErrorToast('Invalid email or password. Please try again.');
         } else if (error.code === 'auth/too-many-requests') {
            showErrorToast(
               'Too many failed login attempts. Please try again later or reset your password.'
            );
         } else {
            showErrorToast(error.message || error.error || 'Failed to sign in. Please try again.');
         }
         setLoading(false);
      } finally {
         setLoading(false);
      }
   };

   return (
      <AuthLayout>
         <Box sx={{ width: '100%', maxWidth: 420, mx: 'auto' }}>
            <Card
               elevation={6}
               sx={{
                  p: { xs: 2, sm: 4 },
                  borderRadius: 4,
                  boxShadow: 6,
                  mt: { xs: 2, md: 0 },
                  mb: 2,
                  bgcolor: 'background.paper',
                  position: 'relative',
                  overflow: 'visible',
               }}
            >
               <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                  <Chip
                     icon={<CelebrationIcon />}
                     label='For individuals & organizations'
                     color='secondary'
                     variant='outlined'
                     sx={{ fontWeight: 600, fontSize: '0.95rem', px: 1.5, py: 0.5 }}
                  />
               </Box>

               {/* Invitation Info */}
               {invitationData && (
                  <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
                     <CardContent>
                        <Typography variant='h6' gutterBottom>
                           🎉 You're Invited!
                        </Typography>
                        <Typography variant='body1' gutterBottom>
                           <strong>{invitationData.inviterName}</strong> invited you to join{' '}
                           <strong>
                              "
                              {invitationData.tripTitle ||
                                 invitationData.eventTitle ||
                                 invitationData.albumTitle}
                              "
                           </strong>
                        </Typography>
                        {invitationData.customMessage && (
                           <>
                              <Divider sx={{ my: 1 }} />
                              <Typography variant='body2' color='text.secondary'>
                                 Message: {invitationData.customMessage}
                              </Typography>
                           </>
                        )}
                     </CardContent>
                  </Card>
               )}

               <Typography
                  variant='h4'
                  component='h1'
                  fontWeight='bold'
                  color='primary'
                  align='center'
               >
                  Welcome Back!
               </Typography>
               <Typography
                  variant='subtitle1'
                  color='text.secondary'
                  align='center'
                  sx={{ mt: 1, mb: 2 }}
               >
                  {invitationData
                     ? 'Sign in to accept your invitation'
                     : 'Sign in to continue your journey'}
               </Typography>

               {/* Login Tabs */}
               <Tabs
                  value={tabValue}
                  onChange={(e, newValue) => setTabValue(newValue)}
                  centered
                  sx={{ mb: 2 }}
               >
                  <Tab label='Email' icon={<EmailIcon />} />
                  <Tab label='Phone' icon={<PhoneIcon />} />
               </Tabs>
               {/* Toast notifications replaced the inline alerts */}
               {/* Email Login Form */}
               {tabValue === 0 && (
                  <form onSubmit={handleLogin} autoComplete='on'>
                     <TextField
                        fullWidth
                        label='Email Address'
                        type='email'
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        error={!!emailError}
                        helperText={emailError}
                        sx={{ mb: 2 }}
                        InputProps={{
                           startAdornment: (
                              <InputAdornment position='start'>
                                 <EmailIcon color='primary' />
                              </InputAdornment>
                           ),
                        }}
                        required
                     />
                     <TextField
                        fullWidth
                        label='Password'
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        error={!!passwordError}
                        helperText={passwordError}
                        sx={{ mb: 2 }}
                        InputProps={{
                           startAdornment: (
                              <InputAdornment position='start'>
                                 <LockIcon color='primary' />
                              </InputAdornment>
                           ),
                           endAdornment: (
                              <InputAdornment position='end'>
                                 <IconButton
                                    onClick={() => setShowPassword(!showPassword)}
                                    edge='end'
                                 >
                                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                 </IconButton>
                              </InputAdornment>
                           ),
                        }}
                        required
                     />
                     <Box sx={{ mt: 1, textAlign: 'right' }}>
                        <Link
                           component='button'
                           type='button'
                           variant='body2'
                           onClick={() => navigate('/reset-password-email')}
                        >
                           Forgot password?
                        </Link>
                     </Box>
                     <Button
                        type='submit'
                        fullWidth
                        variant='contained'
                        size='large'
                        disabled={loading}
                        sx={{ mt: 3, fontWeight: 700, borderRadius: 2 }}
                     >
                        {loading ? <CircularProgress size={24} /> : 'Sign In'}
                     </Button>
                  </form>
               )}

               {/* Phone Login Form */}
               {tabValue === 1 && (
                  <Box>
                     <Box sx={{ mb: 2 }}>
                        <PhoneInput
                           phoneNumber={phoneNumber}
                           selectedCountry={selectedCountry}
                           onPhoneNumberChange={setPhoneNumber}
                           onCountryChange={setSelectedCountry}
                           disabled={phoneNumber !== '' && phoneNumber.includes('+')}
                           placeholder='Enter phone number'
                           label='Phone Number'
                        />
                     </Box>

                     {!codeSent ? (
                        <Button
                           fullWidth
                           variant='contained'
                           size='large'
                           onClick={sendPhoneVerificationCode}
                           disabled={loading}
                           startIcon={loading ? <CircularProgress size={20} /> : <SecurityIcon />}
                           sx={{ mt: 2, fontWeight: 700, borderRadius: 2 }}
                        >
                           {loading ? 'Sending Code...' : 'Send Verification Code'}
                        </Button>
                     ) : (
                        <>
                           <TextField
                              fullWidth
                              label='Verification Code'
                              value={verificationCode}
                              onChange={(e) => setVerificationCode(e.target.value)}
                              placeholder='Enter 6-digit code'
                              inputProps={{ maxLength: 6 }}
                              sx={{ mb: 2 }}
                           />
                           <Button
                              fullWidth
                              variant='contained'
                              size='large'
                              onClick={verifyPhoneAndLogin}
                              disabled={loading || verificationCode.length !== 6}
                              sx={{ mt: 1, fontWeight: 700, borderRadius: 2 }}
                           >
                              {loading ? <CircularProgress size={24} /> : 'Verify & Sign In'}
                           </Button>
                           <Button
                              fullWidth
                              variant='text'
                              onClick={sendPhoneVerificationCode}
                              disabled={loading}
                              sx={{ mt: 1 }}
                           >
                              Resend Code
                           </Button>
                        </>
                     )}
                  </Box>
               )}
               <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography variant='body2' color='text.secondary'>
                     Don't have an account?{' '}
                     <Link component={RouterLink} to='/signup' fontWeight='medium'>
                        Sign up
                     </Link>
                  </Typography>
               </Box>
            </Card>
            {/* Toast Notification */}
            <Snackbar
               open={showToast}
               autoHideDuration={toastSeverity === 'success' ? 4000 : 6000}
               onClose={handleToastClose}
               anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
               <Alert
                  onClose={handleToastClose}
                  severity={toastSeverity}
                  variant='filled'
                  sx={{ width: '100%' }}
               >
                  {toastMessage}
               </Alert>
            </Snackbar>
         </Box>
      </AuthLayout>
   );
};

export default LoginPage;
